import { createTheme } from '@mui/material/styles';

export const tokens = {
  bg: '#08110d',
  bgAlt: '#0d1712',
  surface: '#122019',
  surfaceElevated: '#17271f',
  surfaceSoft: '#1b2d24',
  border: 'rgba(188, 204, 166, 0.14)',
  borderStrong: 'rgba(188, 204, 166, 0.26)',
  text: '#f3ecdf',
  textSoft: '#c8c1b4',
  textMuted: '#8c9788',
  accent: '#a9bf7a',
  accentStrong: '#c5d798',
  clay: '#c98b67',
  cream: '#f5efe4',
  danger: '#e67c73',
  success: '#7bc48e',
  shadow: '0 24px 60px rgba(0, 0, 0, 0.28)',
  gradientHero:
    'radial-gradient(circle at top left, rgba(134, 165, 109, 0.18), transparent 36%), radial-gradient(circle at top right, rgba(201, 139, 103, 0.12), transparent 22%), linear-gradient(180deg, #0c1712 0%, #08110d 100%)',
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: tokens.accent,
      light: tokens.accentStrong,
      dark: '#82975a',
      contrastText: tokens.bg,
    },
    secondary: {
      main: tokens.clay,
      light: '#ddb097',
      dark: '#ab6d4b',
    },
    success: {
      main: tokens.success,
    },
    error: {
      main: tokens.danger,
    },
    background: {
      default: tokens.bg,
      paper: tokens.surface,
    },
    text: {
      primary: tokens.text,
      secondary: tokens.textMuted,
    },
    divider: tokens.border,
  },
  typography: {
    fontFamily: '"Oxygen", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: {
      fontFamily: '"Syne", "Oxygen", sans-serif',
      fontWeight: 700,
      lineHeight: 1.02,
      letterSpacing: '-0.022em',
    },
    h2: {
      fontFamily: '"Syne", "Oxygen", sans-serif',
      fontWeight: 700,
      lineHeight: 1.04,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontFamily: '"Syne", "Oxygen", sans-serif',
      fontWeight: 700,
      lineHeight: 1.08,
      letterSpacing: '-0.016em',
    },
    h4: {
      fontFamily: '"Syne", "Oxygen", sans-serif',
      fontWeight: 700,
      lineHeight: 1.1,
      letterSpacing: '-0.012em',
    },
    h5: {
      fontFamily: '"Syne", "Oxygen", sans-serif',
      fontWeight: 600,
      lineHeight: 1.12,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontFamily: '"Syne", "Oxygen", sans-serif',
      fontWeight: 600,
      lineHeight: 1.14,
      letterSpacing: '-0.008em',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    body1: {
      lineHeight: 1.7,
    },
    body2: {
      lineHeight: 1.65,
    },
  },
  shape: {
    borderRadius: 18,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.bg,
          backgroundImage: tokens.gradientHero,
          color: tokens.text,
        },
        '#root': {
          minHeight: '100vh',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          padding: '11px 20px',
          boxShadow: 'none',
          fontSize: '0.95rem',
          transition: 'transform 160ms ease, background-color 160ms ease, border-color 160ms ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: 'none',
          },
        },
        contained: {
          backgroundColor: tokens.cream,
          color: tokens.bg,
          '&:hover': {
            backgroundColor: '#ebe2d0',
          },
        },
        outlined: {
          borderColor: tokens.borderStrong,
          color: tokens.text,
          backgroundColor: 'rgba(255,255,255,0.02)',
          '&:hover': {
            borderColor: 'rgba(188, 204, 166, 0.38)',
            backgroundColor: 'rgba(255,255,255,0.04)',
          },
        },
        text: {
          color: tokens.textSoft,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(18, 32, 25, 0.9)',
          backgroundImage: 'none',
          border: `1px solid ${tokens.border}`,
          boxShadow: tokens.shadow,
          borderRadius: 24,
          backdropFilter: 'blur(18px)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: tokens.surface,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            borderRadius: 14,
            backgroundColor: 'rgba(255,255,255,0.02)',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.border,
          },
          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.borderStrong,
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.accent,
            borderWidth: 1,
          },
          '& .MuiInputLabel-root': {
            color: tokens.textMuted,
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: tokens.accent,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(8, 17, 13, 0.82)',
          backdropFilter: 'blur(18px)',
          borderBottom: `1px solid ${tokens.border}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(10, 20, 15, 0.92)',
          borderRight: `1px solid ${tokens.border}`,
          backdropFilter: 'blur(20px)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${tokens.border}`,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: tokens.border,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${tokens.border}`,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          backgroundColor: 'rgba(255,255,255,0.06)',
        },
      },
    },
  },
});

export default theme;
